import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Copy,
  Check,
  ChevronDown,
  LoaderCircle,
  CircleCheck,
  TriangleAlert,
  Trash2,
  CircleX,
  CircleMinus,
  RefreshCw,
  ExternalLink,
} from "lucide-react";
import {
  getApp,
  getAppInstalls,
  deleteApp,
  uninstallFromShop,
  getWebhookStatus,
  fixWebhook,
  getInstallUrl,
  type App,
  type Install,
  type WebhookStatus,
} from "../api";
import { useToast } from "../components/Toast";
import Timestamp from "../components/Timestamp";
import Tooltip from "../components/Tooltip";
import PageHeader from "../components/PageHeader";
import AppSwitcher from "../components/AppSwitcher";
import Button from "../components/Button";
import { useAuth } from "../contexts/AuthContext";
import { UI_TIMING } from "../config/constants";
import { usePageTitle } from "../hooks/usePageTitle";

// Helper to format scopes as a bulleted list for tooltips
function ScopesTooltip({
  title,
  scopes,
  extra,
}: {
  title: string;
  scopes: string | undefined;
  extra?: React.ReactNode;
}) {
  const scopeList = scopes
    ?.split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div className="text-left">
      <div className="font-semibold mb-1">{title}</div>
      {extra && <div className="mb-2">{extra}</div>}
      {scopeList && scopeList.length > 0 ? (
        <ul className="space-y-0.5">
          {scopeList.map((scope, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className="text-gray-400 dark:text-dark-text-muted">•</span>
              <span className="font-mono">{scope}</span>
            </li>
          ))}
        </ul>
      ) : (
        <span className="text-gray-400 dark:text-dark-text-muted italic">No scopes recorded</span>
      )}
    </div>
  );
}

// Helper for webhook tooltip showing topics
function WebhookTooltip({
  title,
  topics,
  message,
}: {
  title: string;
  topics?: string[];
  message?: string;
}) {
  return (
    <div className="text-left">
      <div className="font-semibold mb-1">{title}</div>
      {message && <div className="text-amber-600 dark:text-amber-400 mb-1">{message}</div>}
      {topics && topics.length > 0 ? (
        <ul className="space-y-0.5">
          {topics.map((topic, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className="text-gray-400 dark:text-dark-text-muted">•</span>
              <span className="font-mono">{topic}</span>
            </li>
          ))}
        </ul>
      ) : (
        !message && (
          <span className="text-gray-400 dark:text-dark-text-muted italic">
            No webhooks registered
          </span>
        )
      )}
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), UI_TIMING.copyFeedbackMs);
  };

  return (
    <Tooltip content={copied ? "Copied!" : "Copy to clipboard"} position="top">
      <button
        onClick={handleCopy}
        className="p-1 rounded hover:bg-white/50 dark:hover:bg-white/10 transition-colors flex-shrink-0"
        aria-label="Copy to clipboard"
      >
        {copied ? (
          <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
        ) : (
          <Copy className="w-4 h-4 text-gray-400 dark:text-dark-text-muted" />
        )}
      </button>
    </Tooltip>
  );
}

function CopyableText({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), UI_TIMING.copyFeedbackMs);
  };

  return (
    <span className="inline-flex items-center gap-1.5 group">
      <span className="text-sm text-gray-900 dark:text-dark-text font-mono">{value}</span>
      <Tooltip content={copied ? "Copied!" : "Copy to clipboard"} position="top">
        <button
          onClick={handleCopy}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary transition-colors opacity-0 group-hover:opacity-100"
          aria-label="Copy to clipboard"
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
          ) : (
            <Copy className="w-4 h-4 text-gray-400 dark:text-dark-text-muted" />
          )}
        </button>
      </Tooltip>
    </span>
  );
}

function UrlField({
  label,
  description,
  value,
  highlight = false,
  compact = false,
}: {
  label?: string;
  description?: string;
  value: string;
  highlight?: boolean;
  compact?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), UI_TIMING.copyFeedbackMs);
  };

  if (compact) {
    return (
      <button
        onClick={handleCopy}
        className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-dark-bg-tertiary hover:bg-gray-200 dark:hover:bg-dark-border rounded-lg transition-colors font-medium dark:text-dark-text"
      >
        {copied ? "Copied!" : "Copy URL"}
      </button>
    );
  }

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
          {label}
        </label>
      )}
      {description && (
        <p className="text-xs text-gray-500 dark:text-dark-text-muted mb-1">{description}</p>
      )}
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
          highlight
            ? "bg-primary-50 dark:bg-primary-900/20"
            : "bg-gray-100 dark:bg-dark-bg-tertiary"
        }`}
      >
        <code
          className={`text-sm font-mono whitespace-nowrap overflow-x-auto flex-1 ${
            highlight
              ? "text-primary-700 dark:text-primary-300"
              : "text-gray-700 dark:text-dark-text"
          }`}
        >
          {value}
        </code>
        <Tooltip content={copied ? "Copied!" : "Copy to clipboard"} position="top">
          <button
            onClick={handleCopy}
            className="p-1 rounded hover:bg-white/50 dark:hover:bg-white/10 transition-colors flex-shrink-0"
            aria-label="Copy to clipboard"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
            ) : (
              <Copy
                className={`w-4 h-4 ${highlight ? "text-primary-400 dark:text-primary-400" : "text-gray-400 dark:text-dark-text-muted"}`}
              />
            )}
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

export default function AppDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { canEditApp } = useAuth();
  const [app, setApp] = useState<App | null>(null);

  usePageTitle(app?.name ?? null);

  const [installs, setInstalls] = useState<Install[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmUninstallId, setConfirmUninstallId] = useState<string | null>(null);
  const [uninstallingId, setUninstallingId] = useState<string | null>(null);
  const [installCardExpanded, setInstallCardExpanded] = useState(false);
  const [webhookStatus, setWebhookStatus] = useState<
    Record<string, WebhookStatus | "loading" | "error">
  >({});
  const [fixingWebhookId, setFixingWebhookId] = useState<string | null>(null);
  const [generatingInstallUrl, setGeneratingInstallUrl] = useState(false);
  const [reauthorizingId, setReauthorizingId] = useState<string | null>(null);
  const pendingRefreshRef = useRef(false);

  const refreshInstalls = useCallback(async () => {
    if (!id) return;
    try {
      const installsData = await getAppInstalls(id);
      setInstalls(installsData);
      // Clear webhook status cache to force re-fetch
      sessionStorage.removeItem(`webhook-status-${id}`);
      setWebhookStatus({});
    } catch {
      // Silent fail - data will refresh on next page load
    }
  }, [id]);

  // Auto-refresh installs when page regains focus after re-authorization
  useEffect(() => {
    function handleFocus() {
      if (pendingRefreshRef.current) {
        pendingRefreshRef.current = false;
        refreshInstalls();
        addToast("Install data refreshed", "success");
      }
    }

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refreshInstalls, addToast]);

  useEffect(() => {
    if (id) {
      loadData(id);
    }
  }, [id]);

  async function loadData(appId: string) {
    try {
      setLoading(true);
      const [appData, installsData] = await Promise.all([getApp(appId), getAppInstalls(appId)]);
      setApp(appData);
      setInstalls(installsData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load app");
    } finally {
      setLoading(false);
    }
  }

  // Auto-load webhook status for active installs (with sessionStorage caching, 5-min TTL)
  useEffect(() => {
    if (!id || installs.length === 0) return;

    const activeInstalls = installs.filter((i) => !i.uninstalledAt);
    if (activeInstalls.length === 0) return;

    const cacheKey = `webhook-status-${id}`;
    const cacheTTL = 5 * 60 * 1000; // 5 minutes
    const cached = sessionStorage.getItem(cacheKey);
    const cacheEntry = cached ? JSON.parse(cached) : { timestamp: 0, data: {} };
    const isExpired = Date.now() - cacheEntry.timestamp > cacheTTL;
    const rawCachedData = isExpired ? {} : cacheEntry.data;

    // Filter cache to only include current active installs (removes stale entries)
    const cachedData: Record<string, WebhookStatus> = {};
    for (const install of activeInstalls) {
      if (rawCachedData[install.id]) {
        cachedData[install.id] = rawCachedData[install.id];
      }
    }

    // Load from cache first (if not expired)
    if (Object.keys(cachedData).length > 0) {
      setWebhookStatus(cachedData);
    }

    // Check uncached installs (or all if cache expired)
    const uncachedInstalls = activeInstalls.filter((i) => !cachedData[i.id]);
    if (uncachedInstalls.length === 0) return;

    // Fetch webhook status for uncached installs
    Promise.all(
      uncachedInstalls.map(async (install) => {
        try {
          const status = await getWebhookStatus(id, install.id);
          return { installId: install.id, status };
        } catch {
          return { installId: install.id, status: "error" as const };
        }
      })
    ).then((results) => {
      const newStatus: Record<string, WebhookStatus | "error"> = {};
      for (const { installId, status } of results) {
        newStatus[installId] = status;
      }
      setWebhookStatus((prev) => {
        const merged = { ...prev, ...newStatus };
        // Cache successful results with timestamp
        const toCache: Record<string, WebhookStatus> = {};
        for (const [key, val] of Object.entries(merged)) {
          if (val !== "loading" && val !== "error") {
            toCache[key] = val;
          }
        }
        sessionStorage.setItem(cacheKey, JSON.stringify({ timestamp: Date.now(), data: toCache }));
        return merged;
      });
    });
  }, [id, installs]);

  async function handleDelete() {
    if (!id || !app) return;

    setDeleting(true);
    try {
      await deleteApp(id);
      addToast(`App "${app.name}" deleted`, "success");
      navigate("/apps");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete app";
      addToast(message, "error");
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  async function handleUninstall(installId: string) {
    if (!id) return;

    const install = installs.find((i) => i.id === installId);
    setUninstallingId(installId);
    try {
      await uninstallFromShop(id, installId);
      addToast(`Uninstalled from ${install?.shop || "shop"}`, "success");
      await loadData(id);
      setConfirmUninstallId(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to uninstall app";
      addToast(message, "error");
    } finally {
      setUninstallingId(null);
    }
  }

  async function checkWebhookStatus(installId: string) {
    if (!id) return;

    setWebhookStatus((prev) => ({ ...prev, [installId]: "loading" }));
    try {
      const status = await getWebhookStatus(id, installId);
      setWebhookStatus((prev) => ({ ...prev, [installId]: status }));
    } catch {
      setWebhookStatus((prev) => ({ ...prev, [installId]: "error" }));
    }
  }

  async function handleFixWebhook(installId: string) {
    if (!id) return;

    setFixingWebhookId(installId);
    try {
      await fixWebhook(id, installId);
      addToast("Webhook registered successfully", "success");
      // Re-check the status
      await checkWebhookStatus(installId);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fix webhook";
      addToast(message, "error");
    } finally {
      setFixingWebhookId(null);
    }
  }

  async function handleReauthorize(install: Install) {
    if (!id || !app) return;

    setReauthorizingId(install.id);
    try {
      const { url } = await getInstallUrl(id, install.shop);
      // Set flag to refresh data when user returns to this tab
      pendingRefreshRef.current = true;
      window.open(url, "_blank");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to generate re-authorization URL";
      addToast(message, "error");
    } finally {
      setReauthorizingId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-dark-text-secondary">Loading...</div>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
        App not found
      </div>
    );
  }

  const callbackUrl = `${window.location.origin}/api/callback`;
  const canEdit = canEditApp(app);

  return (
    <div>
      <PageHeader>
        <PageHeader.Breadcrumb to="/apps" />
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}
        <PageHeader.TitleRow>
          <PageHeader.TitleGroup>
            <AppSwitcher currentApp={app} />
            <PageHeader.Subtitle>
              <Timestamp date={app.createdAt} prefix="Registered" />
              {app.createdBy && <span> by {app.createdBy}</span>}
            </PageHeader.Subtitle>
            {app.shopifyDashboardUrl && (
              <a
                href={app.shopifyDashboardUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 transition-colors mt-1"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Shopify Dev Dashboard
              </a>
            )}
          </PageHeader.TitleGroup>
          {canEdit && (
            <PageHeader.Actions>
              <Button variant="secondary" as={Link} to={`/apps/${id}/edit`}>
                Edit
              </Button>
              {showDeleteConfirm ? (
                <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-2">
                  <span className="text-sm text-red-700 dark:text-red-300">Delete this app?</span>
                  <Button size="sm" variant="danger" onClick={handleDelete} disabled={deleting}>
                    {deleting ? "Deleting..." : "Yes, Delete"}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleting}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                  Delete App
                </Button>
              )}
            </PageHeader.Actions>
          )}
        </PageHeader.TitleRow>
      </PageHeader>

      {/* Complete Setup Banner - only show when no installs yet */}
      {canEdit && installs.length === 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2">
            Complete Setup
          </h2>
          <div className="space-y-4">
            <div className="text-gray-600 dark:text-dark-text-secondary">
              <span className="font-semibold text-gray-900 dark:text-dark-text">1.</span> Go to{" "}
              <a
                href="https://dev.shopify.com/dashboard"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-500 hover:text-primary-600 dark:text-primary-400"
              >
                dev.shopify.com/dashboard
              </a>{" "}
              &rarr; Your app &rarr; Versions &rarr; Create version. Copy the scopes and URLs from
              the cards below, then click <span className="font-medium">Release</span>.
              <div className="flex items-start gap-2 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg px-3 py-2 mt-2">
                <TriangleAlert className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800 dark:text-amber-300 mb-1">
                    Important:
                  </p>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-800 dark:text-amber-300">
                    <li>Check &ldquo;Use legacy install flow&rdquo;</li>
                    <li>Uncheck &ldquo;Embedded in Shopify admin&rdquo;</li>
                  </ul>
                </div>
              </div>
            </div>
            <p className="text-gray-600 dark:text-dark-text-secondary">
              <span className="font-semibold text-gray-900 dark:text-dark-text">2.</span> Go to{" "}
              <a
                href="https://partners.shopify.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-500 hover:text-primary-600 dark:text-primary-400"
              >
                partners.shopify.com
              </a>{" "}
              &rarr; Apps &rarr; Distribution &rarr; Custom distribution. Enter the merchant&apos;s{" "}
              <code className="text-xs bg-white/50 dark:bg-dark-bg-tertiary px-1 py-0.5 rounded">
                .myshopify.com
              </code>{" "}
              domain and generate the install link.
            </p>
            <p className="text-gray-600 dark:text-dark-text-secondary">
              <span className="font-semibold text-gray-900 dark:text-dark-text">3.</span> Use the
              install link from the Partner Dashboard to install on the merchant&apos;s store.
            </p>
          </div>
        </div>
      )}

      {/* Description card - only shown when description exists */}
      {app.description && (
        <div className="bg-white/60 dark:bg-dark-bg-secondary/60 rounded-xl border border-gray-200/80 dark:border-dark-border/80 px-6 py-4 mb-6">
          <p className="text-sm text-gray-600 dark:text-dark-text-secondary">{app.description}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* App Details */}
        <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4">
            App Details
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-dark-text-secondary mb-1">
                Client ID
              </label>
              <div className="flex items-center gap-2 bg-gray-100 dark:bg-dark-bg-tertiary px-3 py-2 rounded-lg">
                <code className="text-sm font-mono flex-1 dark:text-dark-text">{app.clientId}</code>
                <CopyButton value={app.clientId} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-dark-text-secondary mb-1">
                Client Secret
              </label>
              <code className="block text-sm bg-gray-100 dark:bg-dark-bg-tertiary px-3 py-2 rounded-lg font-mono dark:text-dark-text">
                ••••••••{app.clientSecretLast4}
              </code>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-dark-text-secondary mb-1">
                Scopes
              </label>
              <div className="flex items-start gap-2 bg-gray-100 dark:bg-dark-bg-tertiary px-3 py-2 rounded-lg">
                <div className="flex flex-wrap gap-1.5 flex-1">
                  {app.scopes.split(",").map((scope, i) => (
                    <code
                      key={i}
                      className="text-sm font-mono px-2 py-0.5 bg-white dark:bg-dark-bg-secondary rounded text-gray-700 dark:text-dark-text"
                    >
                      {scope.trim()}
                    </code>
                  ))}
                </div>
                <CopyButton value={app.scopes} />
              </div>
            </div>
          </div>
        </div>

        {/* Shopify Partner Dashboard URLs */}
        <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4">
            Shopify Partner Dashboard URLs
          </h2>
          <p className="text-sm text-gray-500 dark:text-dark-text-secondary mb-4">
            Copy these URLs into your app configuration in Shopify Partner Dashboard.
          </p>

          <div className="space-y-4">
            <UrlField
              label="App URL"
              description="Configuration → URLs → App URL (paste as-is, Shopify replaces {{shop}})"
              value={`${window.location.origin}/api/app?app=${app.id}&shop={{shop}}`}
            />

            <UrlField
              label="Allowed redirection URL(s)"
              description="Configuration → URLs → Allowed redirection URL(s)"
              value={`${callbackUrl}?app=${app.id}`}
            />
          </div>
        </div>
      </div>

      {/* Install / Re-authorize (collapsible, for users who can edit) */}
      {canEdit && (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-sm border border-gray-200 dark:border-dark-border mb-8 overflow-hidden">
          <button
            onClick={() => setInstallCardExpanded(!installCardExpanded)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary transition-colors"
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
              Install / Re-authorize
            </h2>
            <ChevronDown
              className={`w-5 h-5 text-gray-400 dark:text-dark-text-muted transition-transform ${
                installCardExpanded ? "rotate-180" : ""
              }`}
            />
          </button>
          {installCardExpanded && (
            <div className="px-6 pb-6 border-t border-gray-100 dark:border-dark-border pt-4">
              <div className="flex gap-3">
                <input
                  type="text"
                  id="shopDomain"
                  placeholder="store-name.myshopify.com"
                  autoComplete="off"
                  data-1p-ignore
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none font-mono text-sm bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-text-muted"
                />
                <button
                  onClick={async () => {
                    const shopInput = document.getElementById("shopDomain") as HTMLInputElement;
                    const shop = shopInput.value.trim();
                    if (!shop) {
                      addToast("Please enter a shop domain", "error");
                      return;
                    }
                    setGeneratingInstallUrl(true);
                    try {
                      const { url } = await getInstallUrl(app.id!, shop);
                      // Set flag to refresh data when user returns to this tab
                      pendingRefreshRef.current = true;
                      window.open(url, "_blank");
                    } catch (err) {
                      const message =
                        err instanceof Error ? err.message : "Failed to generate install URL";
                      addToast(message, "error");
                    } finally {
                      setGeneratingInstallUrl(false);
                    }
                  }}
                  disabled={generatingInstallUrl}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-500 transition-colors whitespace-nowrap disabled:opacity-50"
                >
                  {generatingInstallUrl ? "Generating..." : "Open Install Link"}
                </button>
              </div>
              <p className="text-xs text-gray-400 dark:text-dark-text-muted mt-3">
                Not seeing the token page after install? Make sure{" "}
                <span className="font-medium">Use legacy install flow</span> is enabled in your
                Shopify app&apos;s version settings.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Install History */}
      <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
            Install History
          </h2>
        </div>

        {installs.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500 dark:text-dark-text-secondary">
            No installations yet
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
            <thead className="bg-gray-50 dark:bg-dark-bg-tertiary">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider w-full">
                  Shop
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider whitespace-nowrap">
                  Installed
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider whitespace-nowrap">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider whitespace-nowrap">
                  Webhooks
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border">
              {installs.map((install) => (
                <tr key={install.id}>
                  <td className="px-6 py-4 whitespace-nowrap align-middle">
                    <CopyableText value={install.shop} />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center align-middle text-sm text-gray-500 dark:text-dark-text-secondary">
                    <Timestamp date={install.installedAt} />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center align-middle">
                    {install.uninstalledAt ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-dark-bg-tertiary text-gray-800 dark:text-dark-text-secondary">
                        <Timestamp date={install.uninstalledAt} prefix="Uninstalled" />
                      </span>
                    ) : install.needsReauthorization ? (
                      <Tooltip
                        content={
                          <ScopesTooltip
                            title="Needs Re-authorization"
                            scopes={install.grantedScopes}
                            extra={
                              <span className="text-amber-600 dark:text-amber-400">
                                {install.grantedScopes
                                  ? "App scopes have changed"
                                  : "Legacy install - re-authorize to track scopes"}
                              </span>
                            }
                          />
                        }
                        position="top"
                      >
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
                          <TriangleAlert className="w-3 h-3" />
                          Needs Update
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        content={
                          <ScopesTooltip title="Granted Scopes" scopes={install.grantedScopes} />
                        }
                        position="top"
                      >
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 cursor-default">
                          Active
                        </span>
                      </Tooltip>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center align-middle">
                    {(() => {
                      const status = webhookStatus[install.id];

                      // For uninstalled apps, show em dash
                      if (install.uninstalledAt) {
                        return <span className="text-gray-400 dark:text-dark-text-muted">—</span>;
                      }

                      // Loading
                      if (status === "loading") {
                        return (
                          <LoaderCircle className="w-5 h-5 text-gray-400 dark:text-dark-text-muted animate-spin mx-auto" />
                        );
                      }

                      // Not checked yet - show loading spinner (auto-load should handle this)
                      if (!status) {
                        return (
                          <LoaderCircle className="w-5 h-5 text-gray-300 dark:text-dark-text-muted animate-spin mx-auto" />
                        );
                      }

                      // Error - red X, clickable to retry
                      if (status === "error") {
                        return (
                          <Tooltip content="Error - click to retry" position="top">
                            <button
                              onClick={() => checkWebhookStatus(install.id)}
                              className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                            >
                              <CircleX className="w-5 h-5" />
                            </button>
                          </Tooltip>
                        );
                      }

                      // Webhook status OK - green checkmark
                      if (status.status === "ok") {
                        return (
                          <Tooltip
                            content={
                              <WebhookTooltip title="Webhooks Registered" topics={status.topics} />
                            }
                            position="top"
                          >
                            <span className="text-green-500 dark:text-green-400 cursor-default">
                              <CircleCheck className="w-5 h-5" />
                            </span>
                          </Tooltip>
                        );
                      }

                      // No token - gray minus
                      if (status.status === "no_token") {
                        return (
                          <Tooltip
                            content={
                              <WebhookTooltip
                                title="No Token"
                                message="Cannot verify webhooks without stored token"
                              />
                            }
                            position="top"
                          >
                            <span className="text-gray-400 dark:text-dark-text-muted cursor-default">
                              <CircleMinus className="w-5 h-5" />
                            </span>
                          </Tooltip>
                        );
                      }

                      // Missing or wrong URL - amber warning, clickable for users who can edit
                      if (status.status === "missing" || status.status === "wrong_url") {
                        const statusMessage =
                          status.status === "missing"
                            ? "Webhook not registered"
                            : "Webhook URL mismatch";
                        const tooltipTitle = canEdit
                          ? `${status.status === "missing" ? "Not Registered" : "URL Mismatch"} - Click to Fix`
                          : status.status === "missing"
                            ? "Not Registered"
                            : "URL Mismatch";

                        const tooltipContent = (
                          <WebhookTooltip title={tooltipTitle} message={statusMessage} />
                        );

                        return canEdit ? (
                          <Tooltip content={tooltipContent} position="top">
                            <button
                              onClick={() => handleFixWebhook(install.id)}
                              disabled={fixingWebhookId === install.id}
                              className="text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 transition-colors disabled:opacity-50"
                            >
                              {fixingWebhookId === install.id ? (
                                <LoaderCircle className="w-5 h-5 animate-spin" />
                              ) : (
                                <TriangleAlert className="w-5 h-5" />
                              )}
                            </button>
                          </Tooltip>
                        ) : (
                          <Tooltip content={tooltipContent} position="top">
                            <span className="text-amber-500 dark:text-amber-400 cursor-default">
                              <TriangleAlert className="w-5 h-5" />
                            </span>
                          </Tooltip>
                        );
                      }

                      return null;
                    })()}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-center align-middle">
                    {canEdit && !install.uninstalledAt && (
                      <div className="inline-flex items-center gap-1">
                        {/* Re-authorize button - shown when scopes need updating */}
                        {install.needsReauthorization && (
                          <Tooltip content="Re-authorize" position="top">
                            <button
                              onClick={() => handleReauthorize(install)}
                              disabled={reauthorizingId === install.id}
                              className="p-1.5 text-amber-500 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors disabled:opacity-50"
                            >
                              {reauthorizingId === install.id ? (
                                <LoaderCircle className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                            </button>
                          </Tooltip>
                        )}

                        {/* Uninstall button with confirmation */}
                        {confirmUninstallId === install.id ? (
                          <div className="inline-flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-1.5">
                            <span className="text-xs text-red-700 dark:text-red-300">
                              Uninstall?
                            </span>
                            <button
                              onClick={() => handleUninstall(install.id)}
                              disabled={uninstallingId === install.id}
                              className="px-2 py-0.5 bg-red-500 text-white text-xs rounded font-medium hover:bg-red-600 transition-colors disabled:opacity-50"
                            >
                              {uninstallingId === install.id ? "..." : "Yes"}
                            </button>
                            <button
                              onClick={() => setConfirmUninstallId(null)}
                              disabled={uninstallingId === install.id}
                              className="px-2 py-0.5 bg-white dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary text-xs rounded font-medium hover:bg-gray-100 dark:hover:bg-dark-border transition-colors border border-gray-300 dark:border-dark-border disabled:opacity-50"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <Tooltip content="Uninstall" position="top">
                            <button
                              onClick={() => setConfirmUninstallId(install.id)}
                              className="p-1.5 text-gray-400 dark:text-dark-text-muted hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
